import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useInfiniteQuery, useQuery, useQueryClient } from '@tanstack/react-query';
import { ApiError } from '../../lib/apiClient.js';
import { queryKeys } from '../../lib/queryKeys.js';
import type { ChatChoiceAnswer } from '@servicecompressionllc/sc-design-system';
import type { AgentThreadSummary, BotQuestion, MessageFeedback } from '../../lib/types.js';
import {
  deleteAgentThread,
  getAgentThreadMessages,
  getCaseThread,
  getConversationFeedback,
  listAgentThreads,
  streamAgentMessage,
  submitAgentFeedback,
} from './agentClient.js';
import {
  NEW_CHAT_TITLE,
  flattenPages,
  markQuestionAnswered,
  mergeConversations,
  titleFromMessage,
  toChatMessages,
  withNewestPage,
  type MessagesPage,
  type ThreadPages,
} from './chatHelpers.js';
import type { ChatMessage, Conversation, ToolActivityEntry } from './types.js';
import { BOT_CHECK_MS } from './useMyBotsLogic.js';
import { useRefreshOnBotPush } from '../push/useRefreshOnBotPush.js';

export interface ChatLogic {
  conversations: Conversation[];
  activeId: string;
  messages: ChatMessage[];
  draft: string;
  /** The active thread's turn is waiting on its first frame. The three turn fields are per thread. */
  isTyping: boolean;
  /** Id of the assistant message currently streaming (render it with `streaming`). */
  streamingMessageId: string | null;
  /** The assistant message of the turn currently IN FLIGHT (from send until the
   *  stream finishes revealing) — its activity card renders live, not settled.
   *  Distinct from streamingMessageId, which starts only at the first text
   *  token: the tool phase and the model's thinking time are live too. */
  liveMessageId: string | null;
  /** Record a 👍/👎 (+ optional detail) on an assistant turn in the active thread. */
  submitFeedback: (input: {
    messageId: string;
    rating: 'up' | 'down';
    comment?: string;
    categories?: string[];
  }) => void;
  /**
   * messageId → the caller's rating, for turns already rated in the active
   * thread. Rehydrated from `/api/feedback/mine` so the control replays its
   * "thanks" state after a reload.
   */
  feedbackByMessage: Record<string, 'up' | 'down'>;
  /** The active thread's prior ratings are still loading (gate the control). */
  isLoadingFeedback: boolean;
  /** A previously-saved conversation's history is being fetched. */
  isLoadingMessages: boolean;
  /** Older messages of the active thread: whether more can load, or the loaded window is full. */
  earlier: { canLoad: boolean; capped: boolean; loading: boolean; load: () => Promise<void> };
  setDraft: (next: string) => void;
  selectConversation: (id: string) => void;
  newConversation: () => void;
  /** Deep-link landing: a fresh thread bound to `caseRef`, kickoff auto-sent. */
  openCaseConversation: (caseRef: string) => void;
  send: (text: string) => void;
  /** Answer the bot's question from its card: a picked option, or their own words. */
  answerQuestion: (question: BotQuestion, answer: ChatChoiceAnswer) => void;
  deleteConversation: (id: string) => void;
}

const messagesKey = queryKeys.agentMessages;
/** Pages held at once (50 messages each): every refetch re-reads all of them, and loading a
 *  page past this would drop the newest one, so earlier messages stop here. */
export const MAX_THREAD_PAGES = 5;
const LAST_ACTIVE_KEY = 'sc-platform:chat:lastActiveId';

const newLocalThread = (): Conversation => ({ id: crypto.randomUUID(), title: NEW_CHAT_TITLE });

/** One thread's turn in flight: typing dots, the bubble revealing text, the live activity card. */
interface TurnState {
  typing: boolean;
  streamingId: string | null;
  liveId: string | null;
}
const IDLE_TURN: TurnState = { typing: false, streamingId: null, liveId: null };

/**
 * Chat state. The server (Mastra memory) is the source of truth for history:
 *  - the conversation list comes from `GET /api/agent/threads`;
 *  - a conversation's messages come from `GET /api/agent/threads/:id/messages`,
 *    cached per thread in React Query;
 *  - the client mints a thread id up front and sends it with each message, so
 *    the UI id and the server thread id are one and the same (no post-hoc
 *    reconciliation), and a send optimistically updates the cached messages.
 * The active thread id is persisted to localStorage so a refresh reopens it.
 * `botThreadIds` are the caller's bot threads: left out of the thread list, so this is how
 * the page knows they're saved conversations to load.
 */
export const useChatLogic = (botThreadIds: readonly string[] = []): ChatLogic => {
  const qc = useQueryClient();

  const threadsQuery = useQuery({
    queryKey: queryKeys.agentThreads(),
    queryFn: async (): Promise<AgentThreadSummary[]> => (await listAgentThreads()).threads,
  });
  const serverThreads = threadsQuery.data ?? [];

  // The URL is the selection: /chat/:conversationId names the open thread, so
  // every conversation is addressable, refreshable, and shareable. The bare '/'
  // falls back to the restored/newest thread until the redirect below stamps it.
  const { conversationId: urlId, shareId } = useParams<{
    conversationId: string;
    shareId: string;
  }>();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  // Drafts that haven't been sent yet have no server thread; hold them locally
  // until the first send persists them (then the server list owns them). Seed a
  // draft only when there's nothing to restore, so a returning user lands on
  // their last conversation instead of a blank one.
  const [localThreads, setLocalThreads] = useState<Conversation[]>(() =>
    localStorage.getItem(LAST_ACTIVE_KEY) ? [] : [newLocalThread()],
  );
  const [restoredId] = useState<string>(() => localStorage.getItem(LAST_ACTIVE_KEY) ?? '');
  const [draft, setDraft] = useState('');
  // Turns in flight, keyed by thread: a turn running in one thread never marks
  // another busy, and one finishing never clears another's.
  const [turns, setTurns] = useState<Record<string, TurnState>>({});
  const updateTurn = (threadId: string, patch: Partial<TurnState>): void =>
    setTurns((prev) => {
      const next = { ...(prev[threadId] ?? IDLE_TURN), ...patch };
      const idle = !next.typing && next.streamingId === null && next.liveId === null;
      return idle
        ? Object.fromEntries(Object.entries(prev).filter(([id]) => id !== threadId))
        : { ...prev, [threadId]: next };
    });

  const conversations = mergeConversations(localThreads, serverThreads);
  // Keep a valid selection: the URL's thread verbatim, else the restored pick
  // if it still exists, else newest.
  const activeId =
    urlId ?? conversations.find((c) => c.id === restoredId)?.id ?? conversations[0]?.id ?? '';
  const activeTurn = turns[activeId] ?? IDLE_TURN;

  // Stamp the fallback selection into the address bar (replace — the bare path
  // isn't a history entry worth keeping). Skipped while a ?case= deep link is
  // being handled: that flow owns its own navigation. Effect on purpose — this
  // syncs React state with the URL, an external system.
  useEffect(() => {
    if (!urlId && !shareId && activeId && !searchParams.get('case')) {
      navigate(`/chat/${activeId}`, { replace: true });
    }
  }, [urlId, shareId, activeId, searchParams, navigate]);

  // Once the list has loaded, guarantee there's always something to compose into
  // (e.g. the persisted thread was deleted, or a first-ever empty account).
  useEffect(() => {
    if (!threadsQuery.isLoading && conversations.length === 0) {
      setLocalThreads([newLocalThread()]);
    }
  }, [threadsQuery.isLoading, conversations.length]);

  const isServerThread =
    serverThreads.some((t) => t.id === activeId) || botThreadIds.includes(activeId);
  // A bot can write while you're away, so its thread is re-read whenever you come back to it,
  // never mid-answer (a refetch would replace the turn still being written).
  const isBotThread = botThreadIds.includes(activeId);
  const turnSettled =
    !activeTurn.typing && activeTurn.liveId === null && activeTurn.streamingId === null;
  useRefreshOnBotPush(activeId, turnSettled);
  const messagesQuery = useInfiniteQuery<
    MessagesPage,
    Error,
    ThreadPages,
    ReturnType<typeof messagesKey>,
    string | null
  >({
    queryKey: messagesKey(activeId),
    queryFn: async ({ pageParam }): Promise<MessagesPage> => {
      const page = await getAgentThreadMessages(activeId, pageParam);
      // Reading a bot thread marks it read on the server, so its pin's count is now stale.
      if (isBotThread && pageParam === null)
        void qc.invalidateQueries({ queryKey: queryKeys.agentBotThreads() });
      return { messages: toChatMessages(page.messages), nextBefore: page.nextBefore };
    },
    // Page 0 is the newest; each next page is older, starting before the last one's oldest.
    initialPageParam: null,
    getNextPageParam: (last) => last.nextBefore,
    maxPages: MAX_THREAD_PAGES,
    enabled: isServerThread && activeId.length > 0,
    ...(isBotThread
      ? {
          staleTime: 0,
          refetchOnWindowFocus: turnSettled,
          refetchInterval: turnSettled ? BOT_CHECK_MS : false,
        }
      : {}),
  });
  const messages = useMemo(() => flattenPages(messagesQuery.data), [messagesQuery.data]);
  const loadedPages = messagesQuery.data?.pages.length ?? 0;
  const earlier = {
    canLoad: messagesQuery.hasNextPage && loadedPages < MAX_THREAD_PAGES,
    capped: messagesQuery.hasNextPage && loadedPages >= MAX_THREAD_PAGES,
    loading: messagesQuery.isFetchingNextPage,
    load: async (): Promise<void> => {
      await messagesQuery.fetchNextPage();
    },
  };

  // The caller's prior ratings for this thread — a separate, non-blocking read
  // (its own query key, sibling of messages) so a feedback-endpoint hiccup never
  // blocks the transcript. Reduced to a messageId→rating map for the control.
  const feedbackQuery = useQuery({
    queryKey: queryKeys.agentFeedback(activeId),
    queryFn: async (): Promise<MessageFeedback[]> =>
      (await getConversationFeedback(activeId)).feedback,
    enabled: isServerThread && activeId.length > 0,
  });
  const feedbackByMessage = useMemo(() => {
    const map: Record<string, 'up' | 'down'> = {};
    for (const f of feedbackQuery.data ?? []) map[f.messageId] = f.rating;
    return map;
  }, [feedbackQuery.data]);

  // Persist the active thread so a refresh reopens it (localStorage only — not
  // server state, so a plain effect is the right tool here).
  useEffect(() => {
    if (activeId) localStorage.setItem(LAST_ACTIVE_KEY, activeId);
  }, [activeId]);

  const selectConversation = (id: string): void => {
    navigate(`/chat/${id}`);
    setDraft('');
  };

  const newConversation = (): void => {
    const convo = newLocalThread();
    setLocalThreads((prev) => [convo, ...prev]);
    setDraft('');
    navigate(`/chat/${convo.id}`);
  };

  // The alert-card deep link's landing. Same card, same chat: an existing
  // conversation for the case is reused (navigate only); otherwise a fresh
  // thread is bound to it with the kickoff auto-sent, so the summary is already
  // streaming when the page settles. Titled for the case up front (matching the
  // server's title) — the canned kickoff would make every case thread look
  // identical. The ?case= param stays in the URL as the marker of what the chat
  // is about; nothing re-sends on refresh because the kickoff only fires here.
  const openCaseConversation = (caseRef: string): void => {
    void (async () => {
      const existing = await getCaseThread(caseRef)
        .then((r) => r.conversationId)
        .catch(() => null); // lookup hiccup → fall through to a fresh thread
      setDraft('');
      if (existing) {
        navigate(`/chat/${existing}?case=${encodeURIComponent(caseRef)}`, { replace: true });
        return;
      }
      const convo: Conversation = { id: crypto.randomUUID(), title: `Case ${caseRef}` };
      setLocalThreads((prev) => [convo, ...prev]);
      sendTo(convo.id, 'Summarize this case: what fired, why, and what should I look at first?', {
        caseRef,
      });
      navigate(`/chat/${convo.id}?case=${encodeURIComponent(caseRef)}`, { replace: true });
    })();
  };

  // If the conversation being removed is the open one, drop the
  // selection so it falls back to the newest thread (or a fresh draft).
  const clearSelectionIfActive = (id: string): void => {
    if (id === activeId) {
      navigate('/', { replace: true });
      setDraft('');
    }
  };

  // A not-yet-sent draft only exists locally — there's no server thread to call.
  const isLocalOnly = (id: string): boolean => !serverThreads.some((t) => t.id === id);

  const refreshThreadLists = (): void => {
    void qc.invalidateQueries({ queryKey: queryKeys.agentThreadsRoot() });
  };

  // A sent thread lives in BOTH lists: its original local draft (kept, just
  // hidden by mergeConversations while it's in serverThreads) AND the server
  // thread. Removing it server-side (delete) un-hides the stale local
  // copy, so we must ALWAYS drop it from localThreads too — otherwise the row
  // "comes back" after the list refetches.
  const dropLocal = (id: string): void =>
    setLocalThreads((prev) => prev.filter((c) => c.id !== id));

  const deleteConversation = (id: string): void => {
    const serverSide = !isLocalOnly(id);
    dropLocal(id);
    clearSelectionIfActive(id);
    if (!serverSide) return;
    void deleteAgentThread(id).then(() => {
      qc.removeQueries({ queryKey: messagesKey(id) });
      refreshThreadLists();
    });
  };

  // Parametrized by thread id so a caller that just created a thread (the
  // morning kickoff in newConversation) can send to it without waiting for the
  // activeId state to settle. `caseRef` binds a brand-new thread to an alert
  // case (the Teams-card deep link); the server ignores it on existing threads.
  const sendTo = (
    id: string,
    text: string,
    opts?: { caseRef?: string; questionAnswer?: { questionId: string; value?: string } },
  ): void => {
    setDraft('');

    // Optimistic: show the user's message immediately, and lock the bot's question it answers.
    qc.setQueryData<ThreadPages>(messagesKey(id), (prev) => {
      const answered = prev && {
        ...prev,
        pages: prev.pages.map((p) => ({
          ...p,
          messages: markQuestionAnswered(p.messages, text, opts?.questionAnswer),
        })),
      };
      return withNewestPage(answered, (newest) => [
        ...newest,
        { id: crypto.randomUUID(), role: 'user', content: text },
      ]);
    });
    // Title an unsent draft from its first message (matches the server's title).
    setLocalThreads((prev) =>
      prev.map((c) =>
        c.id === id && c.title === NEW_CHAT_TITLE ? { ...c, title: titleFromMessage(text) } : c,
      ),
    );

    // Stream the answer in, but reveal it through a SMOOTHING BUFFER rather than
    // painting each delta the instant it lands. The model/proxy deliver tokens in
    // bursts, so painting per-delta reads as choppy; instead we buffer what's
    // received and reveal it at a steady cadence. This does two things: it
    // decouples the type-out from network burstiness (smooth), and it caps the
    // markdown bubble's full re-parse to ~25x/sec instead of once per token.
    // The typing dots show until the first token (covering tool-calling latency).
    const assistantId = crypto.randomUUID();
    updateTurn(id, { typing: true, liveId: assistantId });
    let feedbackId: string | undefined; // assistant message id (from `done`); what feedback attaches to
    let received = ''; // everything the server has sent so far
    let shownLen = 0; // how much of it we've revealed to the UI
    let started = false;
    let streamDone = false;
    let timer: ReturnType<typeof setInterval> | null = null;
    // The turn's tool-activity record: one row per call, updated in place as
    // tool-start/tool-end frames arrive. The DONE label is held aside so the
    // row flips tense ("Searching…" → "Searched…") the moment its call ends.
    const toolActivity: ToolActivityEntry[] = [];
    const doneLabels = new Map<string, string>();

    const paint = (): void => {
      // Raw slice — the bubble is rendered with the DS `streaming` prop, which
      // clips a half-streamed trailing link so its raw URL never flashes.
      const content = received.slice(0, shownLen);
      const activity = toolActivity.length ? toolActivity.map((t) => ({ ...t })) : undefined;
      qc.setQueryData<ThreadPages>(messagesKey(id), (data) =>
        withNewestPage(data, (prev) =>
          prev.some((m) => m.id === assistantId)
            ? prev.map((m) =>
                m.id === assistantId ? { ...m, content, feedbackId, toolActivity: activity } : m,
              )
            : [
                ...prev,
                { id: assistantId, role: 'assistant', content, feedbackId, toolActivity: activity },
              ],
        ),
      );
    };

    const stopTicking = (): void => {
      if (timer !== null) {
        clearInterval(timer);
        timer = null;
      }
    };

    // Each tick reveals a slice proportional to the backlog — so we keep pace
    // with a fast stream yet stay smooth on a slow one — and fully drain on done.
    const tick = (): void => {
      if (shownLen < received.length) {
        const backlog = received.length - shownLen;
        shownLen = Math.min(received.length, shownLen + Math.max(2, Math.ceil(backlog / 6)));
        paint();
      }
      if (streamDone && shownLen >= received.length) {
        stopTicking();
        // Fully revealed and over: drop the streaming flag; the activity settles to the pill.
        updateTurn(id, { streamingId: null, liveId: null });
      }
    };

    const ensureTicking = (): void => {
      if (timer === null) {
        timer = setInterval(tick, 40);
        tick(); // reveal a first slice now, not 40ms from now (no empty-bubble flicker)
      }
    };

    // Send our client-minted id as the thread id (the server uses it as-is).
    // The turn's message exists from the first millisecond: the thinking card
    // renders inside the assistant row, so without this the pre-first-frame
    // gap shows nothing at all (the old dots block is gone).
    paint();

    void streamAgentMessage(
      {
        message: text,
        conversationId: id,
        ...(opts?.caseRef ? { caseRef: opts.caseRef } : {}),
        ...(opts?.questionAnswer ? { questionAnswer: opts.questionAnswer } : {}),
      },
      {
        onToolStart: (start) => {
          // A tool call began — its row appears in the activity card at once
          // (the paint targets the assistant message, creating it if the card
          // starts before any text).
          updateTurn(id, { typing: false });
          doneLabels.set(start.id, start.doneLabel);
          toolActivity.push({
            id: start.id,
            label: start.label,
            ...(start.detail !== null ? { detail: start.detail } : {}),
            status: 'running',
          });
          paint();
        },
        onToolEnd: (end) => {
          const row = toolActivity.find((t) => t.id === end.id);
          if (!row) return;
          row.status = end.ok ? 'done' : 'failed';
          if (end.durationMs !== null) row.durationMs = end.durationMs;
          // Flip tense: the settled row reads as a record, not an action.
          row.label = doneLabels.get(end.id) ?? row.label;
          paint();
        },
        onDelta: (delta) => {
          if (!started) {
            started = true;
            // First token in: drop the dots and mark this bubble as streaming.
            updateTurn(id, { typing: false, streamingId: assistantId });
          }
          received += delta;
          ensureTicking();
        },
        onDone: (info) => {
          updateTurn(id, { typing: false });
          // The assistant message id this turn's feedback attaches to — stamp it
          // so the control appears once the text finishes revealing, and repaint
          // so the already-rendered bubble picks it up.
          feedbackId = info.messageId;
          paint();
          streamDone = true;
          ensureTicking(); // in case everything arrived before the first tick fired
          // The thread now exists server-side / moved to the top — refresh both lists.
          void qc.invalidateQueries({ queryKey: queryKeys.agentThreadsRoot() });
        },
        onError: (err) => {
          stopTicking();
          shownLen = received.length; // flush whatever streamed before the error
          if (received) paint();
          qc.setQueryData<ThreadPages>(messagesKey(id), (data) =>
            withNewestPage(data, (prev) => [
              // A turn that died before producing anything leaves no ghost row.
              ...prev.filter(
                (m) =>
                  m.id !== assistantId || m.content.length > 0 || (m.toolActivity?.length ?? 0) > 0,
              ),
              { id: crypto.randomUUID(), role: 'system', content: errorToMessage(err) },
            ]),
          );
          updateTurn(id, IDLE_TURN);
          // A question it closed already (or never) shows as it really stands.
          if (err instanceof ApiError && err.status === 409) {
            void qc.invalidateQueries({ queryKey: messagesKey(id) });
          }
        },
      },
    );
  };

  const send = (text: string): void => {
    if (activeId) sendTo(activeId, text);
  };

  const answerQuestion = (question: BotQuestion, answer: ChatChoiceAnswer): void => {
    if (!activeId) return;
    const text =
      answer.kind === 'option'
        ? (question.options.find((o) => o.value === answer.value)?.label ?? answer.value)
        : answer.text;
    sendTo(activeId, text, {
      questionAnswer: {
        questionId: question.id,
        ...(answer.kind === 'option' ? { value: answer.value } : {}),
      },
    });
  };

  // Record a 👍/👎 on a turn in the active thread. Optimistically reflect it in
  // the rehydration map so the control stays "rated" across a reload; the POST is
  // fire-and-forget (the control shows its own "thanks"; a failed POST is logged).
  const submitFeedback = (input: {
    messageId: string;
    rating: 'up' | 'down';
    comment?: string;
    categories?: string[];
  }): void => {
    qc.setQueryData<MessageFeedback[]>(queryKeys.agentFeedback(activeId), (prev = []) => [
      ...prev.filter((f) => f.messageId !== input.messageId),
      { messageId: input.messageId, rating: input.rating },
    ]);
    void submitAgentFeedback({ conversationId: activeId, ...input }).catch(() => {});
  };

  return {
    conversations,
    activeId,
    messages,
    draft,
    streamingMessageId: activeTurn.streamingId,
    liveMessageId: activeTurn.liveId,
    submitFeedback,
    feedbackByMessage,
    isLoadingFeedback: isServerThread && feedbackQuery.isLoading,
    isTyping: activeTurn.typing,
    isLoadingMessages: isServerThread && messagesQuery.isLoading,
    earlier,
    setDraft,
    selectConversation,
    newConversation,
    openCaseConversation,
    send,
    answerQuestion,
    deleteConversation,
  };
};

// Maps the orchestrator's auth error codes (packages/orchestration/src/plugins/auth.ts)
// to honest, actionable messages. These all mean "you're not a platform user who's
// allowed to do this" — the app can't fix them; a tenant admin must.
const errorToMessage = (err: unknown): string => {
  if (err instanceof ApiError) {
    switch (err.code) {
      case 'insufficient_scope':
        return "⚠️ You don't have access to the assistant yet — it needs the `Agent.Run` capability. If a tenant admin just granted it, try again in a moment; otherwise ask them to grant it.";
      case 'not_invited':
        return "⚠️ Your account isn't a platform user yet. A tenant admin needs to invite you first.";
      case 'account_deactivated':
        return '⚠️ Your account has been deactivated. Contact a tenant admin.';
      case 'not_in_required_group':
        return "⚠️ Your account isn't in the group required to reach the platform.";
      // Stream-failure codes from the agent route's SSE error events — the
      // upstream AI service refused or the turn died mid-answer. Transient
      // from the user's seat; never show raw codes or billing details.
      case 'assistant_unavailable':
        return '⚠️ The assistant is temporarily unavailable — the AI service rejected the request. Your admins can see the cause in the logs. Please try again in a few minutes.';
      case 'stream_timeout':
        return '⚠️ The assistant took too long to answer and the attempt was stopped. Please try again — a shorter or more specific question often helps.';
      case 'question_closed':
        return '⚠️ That question was already closed, so your answer didn’t go through.';
      case 'stream_failed':
        return '⚠️ The connection dropped mid-answer. Anything shown above still stands; please send your question again.';
      default:
        break;
    }
    if (err.status === 401) {
      return '⚠️ Your session expired. Please sign in again.';
    }
    return `⚠️ The assistant couldn't respond (${err.status}): ${err.message}`;
  }
  // Not an error the server sent: the connection itself dropped.
  return '⚠️ The connection to the assistant dropped before the answer finished. Please send your question again.';
};
